/**
 * Diwali festive theme — Tailwind utility classes + static SVG artwork.
 *
 * Activation: the `DIWALI_THEME_ENABLED` flag below. It puts the
 * `diwali-theme` class on <body> (src/app/layout.tsx) and picks the header
 * logo, which can't be swapped with a class. Every class in this file is
 * gated behind `diwali-theme`, either with the `in-[.diwali-theme]:` variant
 * (element sits inside the themed wrapper) or with `[&.diwali-theme_…]:`
 * (applied on the wrapper itself). Setting the flag to `false` therefore
 * restores the original design, even though the class strings below stay
 * attached to the components.
 *
 * Decorations are aria-hidden, pointer-events-none and rendered behind
 * content (negative z-index), so they never intercept clicks or focus. They
 * render nothing at all when the flag is off.
 *
 * `!` is used only where an unlayered rule in globals.css (e.g.
 * `.desktop-menu a:hover`, `.heading::after`) or an equally specific utility would otherwise
 * win over Tailwind's utilities layer.
 */

import type { CSSProperties } from "react";

/** The single on/off switch for the Diwali theme. */
export const DIWALI_THEME_ENABLED = true;

/** Goes on <body>; empty when the theme is off. */
export const DIWALI_THEME_CLASS = DIWALI_THEME_ENABLED ? "diwali-theme" : "";

/** Header logo file on the static CDN: white lettering for the dark festive header. */
export const HEADER_LOGO_FILE = DIWALI_THEME_ENABLED ? "car-junction-white.svg" : "car-junction-logo.svg";

const cx = (...classes: string[]) => classes.join(" ");

/* ------------------------------------------------------------------ */
/* Palette (for reference; classes below use the literal values so    */
/* Tailwind's scanner can see them)                                   */
/*   Royal Purple #4B1768 · Deep Maroon #650D24 · Golden #FFD700      */
/*   Saffron #FF9933 · Orange #FF6B00 · Festive Pink #E83E8C          */
/*   Deep Background #1D102B                                          */
/* ------------------------------------------------------------------ */

/**
 * Applied on the theme wrapper (<body>) next to `diwali-theme`.
 * Covers site-wide touches: warm page tone and section heading accents.
 */
export const DIWALI_ROOT_CLASS = cx(
    // Warm ivory page tone (body background reads var(--background))
    // "[&.diwali-theme]:[--background:#FFF9EE]",

    // Section heading accents: saffron → gold instead of the default teal
    "[&.diwali-theme_.heading]:after:bg-[linear-gradient(90deg,#FF9933,#FFD700_45%,rgba(255,215,0,0))]!",
    "[&.diwali-theme_.heading-bottom-line]:before:bg-[linear-gradient(90deg,#FF6B00,#FFD700)]!",
);

/** Class additions for the existing <Header>. */
export const DIWALI_HEADER = {
    root: cx(
        "in-[.diwali-theme]:bg-[#4B1768]",
        "in-[.diwali-theme]:bg-[linear-gradient(100deg,#3A0F55_0%,#4B1768_40%,#650D24_100%)]",
        "in-[.diwali-theme]:border-b-[#FFD700]/50",
        // No text colour here: the mobile drawer and modals render inside <header>
        // and must keep inheriting the page's dark text.
        "in-[.diwali-theme]:shadow-[0_6px_24px_-12px_rgba(255,153,51,0.6)]",
        // Desktop search pill: warm ivory with a golden edge
        "in-[.diwali-theme]:[&_nav_input]:border-[#FFD700]/70",
        "in-[.diwali-theme]:[&_nav_input]:bg-[#FFFBF2]",
    ),
    navLink: cx(
        "in-[.diwali-theme]:text-[#FFF1D0]",
        "in-[.diwali-theme]:hover:text-[#FFD700]!",
        "in-[.diwali-theme]:decoration-[#FF9933]",
        "in-[.diwali-theme]:decoration-2",
    ),
    /** Dark glyph icons (arrows, location pin) → light on the purple bar. */
    icon: "in-[.diwali-theme]:brightness-0 in-[.diwali-theme]:invert in-[.diwali-theme]:opacity-90",
    /** Mobile search glyph is darkened by an inline `filter` style, hence `!`. */
    mobileSearchIcon: "in-[.diwali-theme]:brightness-0! in-[.diwali-theme]:invert!",
    menuToggle: "in-[.diwali-theme]:text-[#FFD700] in-[.diwali-theme]:hover:text-[#FF9933]",
    // `!` on hover: the buttons' own hover:bg-gray-* has equal specificity and would win
    iconButton: "in-[.diwali-theme]:hover:bg-[#FFD700]/15!",
    cityButton: cx(
        "in-[.diwali-theme]:bg-white/10",
        "in-[.diwali-theme]:text-[#FFE9A8]",
        "in-[.diwali-theme]:ring-1",
        "in-[.diwali-theme]:ring-[#FFD700]/40",
        "in-[.diwali-theme]:hover:bg-[#FFD700]/20!",
        "in-[.diwali-theme]:hover:text-[#FFD700]",
        "in-[.diwali-theme]:hover:ring-[#FFD700]/80",
    ),
    /** Desktop dropdown panels stay light for readability, with a festive top edge. */
    dropdown: "in-[.diwali-theme]:border-[#FFD700]/40 in-[.diwali-theme]:border-t-2 in-[.diwali-theme]:border-t-[#FF9933]",
    drawer: "in-[.diwali-theme]:bg-[#FFFBF2] in-[.diwali-theme]:border-r-[#FFD700]/60",
};

/** Class additions for the existing <Footer>. */
export const DIWALI_FOOTER = {
    root: cx(
        "in-[.diwali-theme]:relative",
        "in-[.diwali-theme]:isolate",
        "in-[.diwali-theme]:overflow-hidden",
        "in-[.diwali-theme]:bg-[#1D102B]",
        "in-[.diwali-theme]:bg-[linear-gradient(180deg,#650D24_0%,#3A0F4F_45%,#1D102B_100%)]",
        "in-[.diwali-theme]:text-[#F3E3C3]",
        "in-[.diwali-theme]:border-t-2",
        "in-[.diwali-theme]:border-t-[#FFD700]/70",
        // Room for the diya row at the top
        "in-[.diwali-theme]:pt-16!",
    ),
    heading: "in-[.diwali-theme]:text-[#FFD700]",
    link: "in-[.diwali-theme]:text-[#FBEFD5] in-[.diwali-theme]:hover:text-[#FF9933]",
    divider: "in-[.diwali-theme]:border-[#FFD700]/25!",
    chevron: "in-[.diwali-theme]:border-[#FFD700]!",
    social: cx(
        "in-[.diwali-theme]:bg-[#FFD700]/10",
        "in-[.diwali-theme]:border-[#FFD700]/50",
        "in-[.diwali-theme]:hover:bg-[#FFD700]/25",
        "in-[.diwali-theme]:hover:shadow-[0_0_12px_rgba(255,215,0,0.5)]",
    ),
    brandLink: "in-[.diwali-theme]:decoration-[#FFD700] in-[.diwali-theme]:hover:text-[#FFD700]",
};

/* ------------------------------------------------------------------ */
/* Decorative illustrations                                           */
/* ------------------------------------------------------------------ */

/*
 * Artwork is static SVG on the CDN (cj/diwali/), rendered as CSS backgrounds:
 * one DOM node per piece and one cached request per file, instead of ~80
 * inline SVG nodes per rangoli. Tailwind can't build class names from env
 * vars, so the URLs are set as CSS variables on each decor wrapper
 * (DECOR_IMAGES) and read with `bg-(image:--…)`.
 */
const DECOR_IMAGES = {
    "--diwali-rangoli": `url(${process.env.NEXT_PUBLIC_CDN_STATIC_URL}diwali/rangoli.svg)`,
    "--diwali-diya": `url(${process.env.NEXT_PUBLIC_CDN_STATIC_URL}diwali/diya.svg)`,
    "--diwali-lights": `url(${process.env.NEXT_PUBLIC_CDN_STATIC_URL}diwali/lights.svg)`,
} as CSSProperties;

const RANGOLI = "aspect-square bg-(image:--diwali-rangoli) bg-contain bg-center bg-no-repeat";
const DIYA = "aspect-[64/56] bg-(image:--diwali-diya) bg-contain bg-center bg-no-repeat";
const LIGHTS = "h-2 bg-(image:--diwali-lights) bg-size-[72px_8px] bg-repeat-x";

/** Hidden unless the theme is active; never interactive. */
const DECOR_BASE = "pointer-events-none select-none hidden in-[.diwali-theme]:block";

/** Drop inside the existing <header> (a sticky, z-indexed stacking context). */
export function DiwaliHeaderDecor() {
    if (!DIWALI_THEME_ENABLED) return null;
    return (
        <div aria-hidden="true" className={`${DECOR_BASE} absolute inset-0 -z-10 overflow-hidden`} style={DECOR_IMAGES}>
            <div className="absolute inset-0 bg-radial-[at_50%_0%] from-[#FF9933]/25 via-transparent to-transparent" />
            <div className={`${RANGOLI} absolute -left-12 -top-14 w-32 opacity-30 md:w-40`} />
            <div className={`${RANGOLI} absolute -bottom-16 -right-12 w-32 opacity-30 md:w-40`} />
            <div className={`${DIYA} absolute bottom-1.5 left-4 hidden w-9 min-[1400px]:block`} />
            <div className={`${DIYA} absolute bottom-1.5 right-4 hidden w-9 min-[1400px]:block`} />
            <div className={`${LIGHTS} absolute inset-x-6 bottom-0.5 hidden md:block`} />
            <div className="absolute inset-x-0 bottom-0 h-px bg-linear-to-r from-transparent via-[#FFD700] to-transparent" />
        </div>
    );
}

/** Drop inside the existing <footer> (isolated by DIWALI_FOOTER.root). */
export function DiwaliFooterDecor() {
    if (!DIWALI_THEME_ENABLED) return null;
    return (
        <div aria-hidden="true" className={`${DECOR_BASE} absolute inset-0 -z-10 overflow-hidden`} style={DECOR_IMAGES}>
            <div className="absolute inset-0 bg-radial-[at_50%_0%] from-[#FF9933]/20 via-transparent to-transparent" />
            <div className={`${RANGOLI} absolute -left-20 -top-20 w-56 opacity-20 md:w-72`} />
            <div className={`${RANGOLI} absolute -bottom-24 -right-24 w-64 opacity-20 md:w-80`} />
            <div className="absolute inset-x-0 top-3 flex items-end justify-center gap-8 md:gap-12">
                <div className={`${DIYA} hidden w-7 md:block`} />
                <div className={`${DIYA} w-7`} />
                <div className={`${DIYA} w-9`} />
                <div className={`${DIYA} w-7`} />
                <div className={`${DIYA} hidden w-7 md:block`} />
            </div>
            <div className={`${DIYA} absolute bottom-6 left-6 hidden w-10 min-[1400px]:block`} />
            <div className={`${DIYA} absolute bottom-6 right-6 hidden w-10 min-[1400px]:block`} />
        </div>
    );
}
